import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";


/**
 * Save a gallery item in the database.
 * @param {Object} gallery The updated information of the targeted item.
 * @param {Array} media The media files to upload.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const createGallery = async (
  gallery,
  media,
  setState,
  setApiError,
  setIsLoading
) => {
  const requestData = new FormData();
  const arrayOfFiles = [...media];
  requestData.append(
    "gallery",
    new Blob([JSON.stringify(gallery)], { type: "application/json" })
  );

  arrayOfFiles.forEach((file) => requestData.append("media", file));

  await HttpClient.getAxiosClient()
    .post(`${ServerEndpoints.GALLERY_ENDPOINT}`, requestData)
    .then((response) => {
      if (response.status === 201) {
        CustomToast("Gallery item successfully created", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 409:
          CustomToast("A gallery item with the provided id already exist", "error");
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));

};


// --------------------------------- Function to update a gallery item in the database -------------------------

/**
 * Update a gallery item in the database.
 * @param {Object} gallery The updated information of the targeted gallery item.
 * @param {Array} media The media files to upload.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const updateGallery = async (
  gallery,
  media,
  setState,
  setApiError,
  setIsLoading
) => {
  const requestData = new FormData();
  const arrayOfFiles = media.filter((file) => !file.id && !file.blobName);
  requestData.append(
    "gallery",
    new Blob([JSON.stringify(gallery)], { type: "application/json" })
  );

  const retainedMediaIds = media
    .filter((file) => file.id || file.blobName)
    .map((file) => file.id);

  if (retainedMediaIds.length > 0) {
    requestData.append(
      "retainedMediaIds",
      new Blob([JSON.stringify(retainedMediaIds)], { type: "application/json" })
    );
  }

  if (arrayOfFiles.length > 0) {
    arrayOfFiles.forEach((file) => requestData.append("media", file));
  }

  await HttpClient.getAxiosClient()
    .put(`${ServerEndpoints.GALLERY_ENDPOINT}/${gallery.id}`, requestData)
    .then((response) => {
      if (response.status === 200) {
        CustomToast("Gallery item successfully updated", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "The gallery item you are trying to update does not exist in the database.",
            "error"
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));
};


// --------------------------------- Function to delete a gallery item from the database -------------------------

/**
 * Delete a gallery item from the database.
 * @param {String} id The id of the targeted gallery item.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const deleteGallery = async (id, setState, setApiError, setIsLoading) => {
  await HttpClient.getAxiosClient()
    .delete(`${ServerEndpoints.GALLERY_ENDPOINT}/${id}`)
    .then((response) => {
      if (response.status === 200) {
        CustomToast("Gallery item successfully deleted", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        case 404:
          CustomToast(
            "The gallery item you are trying to delete does not exist in the database.",
            "error"
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));
}


// --------------------------------- Function to delete multiple gallery items from the database -------------------------

/**
 * Delete multiple gallery items at once.
 * @param {Array} ids A list of identifiers.
 * @param {Function} setState The function to update the flag status for the page refresh.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const deleteGalleryItems = async (
  ids,
  setState,
  setApiError,
  setIsLoading
) => {
  const requestData = new FormData();
  requestData.append(
    "ids",
    new Blob([JSON.stringify(ids)], { type: "application/json" })
  );

  await HttpClient.getAxiosClient()
    .delete(`${ServerEndpoints.GALLERY_ENDPOINT}`, requestData)
    .then((response) => {
      if (response.status === 204) {
        CustomToast("Items successfully deleted", "success");
        setState((prev) => !prev);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 400:
          CustomToast("Please review your data and try again.", "error");
          break;

        case 404:
          CustomToast(
            "Some of the items you are trying to delete do not exist in the database.",
            "error"
          );
          break;

        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));
};


// --------------------------------- Function to fetch all gallery items from the database -------------------------

/**
 * Fetch all gallery items from the database.
 * @param {Object} params The pagination parameters.
 * @param {Function} setGalleryItems The function to update the gallery items state.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const fetchGalleryItems = async (
  params,
  setApiError,
  setGalleryItems,
  setIsLoading
) => {
  await HttpClient.getAxiosClient()
    .get(`${ServerEndpoints.GALLERY_ENDPOINT}`, { params })
    .then((response) => {
      if (response.status === 200) {
        setGalleryItems(response.data);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));
};


// --------------------------------- Function to fetch a gallery item from the database -------------------------

/**
 * Fetch a gallery item from the database.
 * @param {String} id The id of the targeted gallery item.
 * @param {Function} setGallery The function to update the gallery item state.
 * @param {Function} setApiError The function to update the api error state.
 * @param {Function} setIsLoading The function to update the loading state.
 */
export const fetchGalleryItem = async (
  id,
  setGallery,
  setApiError,
  setIsLoading
) => {
  await HttpClient.getAxiosClient()
    .get(`${ServerEndpoints.GALLERY_ENDPOINT}/${id}`)
    .then((response) => {
      if (response.status === 200) {
        setGallery(response.data);
      }
    })
    .catch((err) => {
      setApiError(true);
      switch (err.response.status) {
        case 503:
          CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
          throw new Error(Errors.SERVICE_UNAVALAIBLE_ERROR);

        case 404:
          CustomToast(
            "The gallery item you are trying to fetch does not exist in the database.",
            "error"
          );
          break;

        default:
          CustomToast(Errors.API_ERROR, "error");
      }
    })
    .finally(() => setIsLoading(false));
};
